import "./config/reown";
// Uniswap V2 Clone with PancakeSwap V2 Contracts
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    Outlet,
} from "react-router-dom";
import { useState, useEffect } from "react";
import { AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import { Toaster } from "react-hot-toast";
import { Header, WalletRequiredCard, BottomNavigator } from "./components";
import ChainSwitchModal from "./components/ChainSwitchModal";
import ReferralBindGateModal from "./components/ReferralBindGateModal";
import WalletDebugPanel from "./components/WalletDebugPanel";
import {
    Community,
    History,
    BonusHistory,
    Deposit,
    DepositHistory,
    Staking,
    Yield,
    YieldHistory,
    StakingHistory,
    Dashboard,
    Swap,
    FreeStaking,
    IncomeReport,
    VaultDetails,
    News,
    NewsDetails,
    VtDeposit
} from "./pages";
import { TOAST_POSITION } from "./constants";
import { LanguageProvider } from "./contexts/LanguageContext";

import { useTranslation } from "./hooks/useTranslation";
import { useWallet } from "./hooks/useWallet";
import { WalletProvider } from "./contexts/WalletContext";

function StandardLayout() {
    const { t } = useTranslation();
    const {
        isWalletDetected,
        isConnected,
        isCorrectNetwork,
        chainId,
        connectWallet,
        switchNetwork,
        isConnecting,
        metaMaskDownloadUrl,
        trustWalletDeepLinkUrl,
        rememberTrustWalletIntent,
        shouldAvoidInstallPrompt,
        walletCheckDone,
        walletConnectAvailable,
        walletConnectProjectIdMissing,
        isTokenPocketBlocked,
        freeStaking,
    } = useWallet();

    // Do not expose protected pages before wallet connection.
    // While wallet detection is still running, show the connect card first;
    // after detection finishes, switch to install only when no Web3 wallet/browser is detected.
    const showWalletCard = !isConnected;
    const walletCardVariant = walletCheckDone && !isWalletDetected && !shouldAvoidInstallPrompt ? "install" : "connect";
    const mustUseBNB = isConnected && chainId != null && !isCorrectNetwork;

    return (
        <>
            <Header />
            <main className="container mx-auto px-4 pt-4 pb-24 md:py-8">
                <div className="max-w-4xl mx-auto">
                    {showWalletCard ? (
                        <div className="flex items-center justify-center min-h-[50vh]">
                            <WalletRequiredCard
                                variant={walletCardVariant}
                                onConnect={connectWallet}
                                isConnecting={isConnecting}
                                metaMaskDownloadUrl={metaMaskDownloadUrl}
                                trustWalletDeepLinkUrl={trustWalletDeepLinkUrl}
                                rememberTrustWalletIntent={rememberTrustWalletIntent}
                                walletDebugEnabled={typeof window !== "undefined" && new URLSearchParams(window.location.search).get("walletDebug") === "1"}
                                walletConnectAvailable={walletConnectAvailable}
                                walletConnectProjectIdMissing={walletConnectProjectIdMissing}
                                isTokenPocketBlocked={isTokenPocketBlocked}
                            />
                        </div>
                    ) : mustUseBNB ? (
                        <div className="flex items-center justify-center min-h-[50vh]">
                            <div className="rounded-2xl bg-[#1a1023]/90 border border-[#9770DD]/30 p-6 sm:p-8 shadow-lg shadow-[#9770DD]/10 max-w-md mx-auto text-center">
                                <h2 className="text-xl sm:text-2xl font-semibold text-white mb-3">
                                    {t("chainSwitch.mustUseBNBChain") || "Must use BNB Chain"}
                                </h2>
                                <p className="text-white/80 text-sm sm:text-base mb-6">
                                    {t("chainSwitch.pleaseSwitchToBNB") || "Please switch your wallet network to BNB Smart Chain to continue."}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => switchNetwork()}
                                    className="w-full py-3 px-6 rounded-xl bg-[#9770DD] hover:bg-[#7a1fd6] text-white font-semibold transition-colors"
                                >
                                    {t("chainSwitch.switchToBSC") || "Switch to BNB Chain"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <Outlet />
                    )}
                </div>
            </main>
            <BottomNavigator />
        </>
    );
}

// --- Main App Component ---
function AppContent() {
    const prefersReducedMotion = useReducedMotion();
    const { t } = useTranslation();
    const { chainId, isCorrectNetwork, isConnected, switchNetwork, freeStaking } = useWallet();
    const [showChainModal, setShowChainModal] = useState(false);

    const normalizeChainId = (id) => {
        if (id == null || id === "") return null;
        if (typeof id === "string") {
            const trimmed = id.trim();
            if (!trimmed) return null;
            if (trimmed.startsWith("0x")) return trimmed.toLowerCase();
            const numeric = Number(trimmed);
            return Number.isFinite(numeric) ? `0x${numeric.toString(16)}` : trimmed;
        }
        const numeric = Number(id);
        return Number.isFinite(numeric) ? `0x${numeric.toString(16)}` : null;
    };

    const getChainName = (id) => {
        const normalizedId = normalizeChainId(id);
        const chains = {
            "0x1": "Ethereum Mainnet",
            "0x38": "BNB Smart Chain",
            "0x61": "BSC Testnet",
            "0x89": "Polygon",
            "0xa86a": "Avalanche",
            "0xfa": "Fantom",
            "0xa4b1": "Arbitrum",
        };
        return chains[normalizedId] || `Unknown Chain (${id})`;
    };

    // Show modal when connected on wrong chain
    useEffect(() => {
        if (isConnected && !isCorrectNetwork && chainId) {
            setShowChainModal(true);
        } else {
            setShowChainModal(false);
        }
    }, [isConnected, isCorrectNetwork, chainId]);

    const handleSwitchChain = async () => {
        const switched = await switchNetwork();
        if (switched) setShowChainModal(false);
    };

    return (
        <div className="min-h-screen pokemon-crm-bg">
            <Toaster position={TOAST_POSITION} />
            <WalletDebugPanel />

            {/* Chain Switch Modal */}
            <ChainSwitchModal
                isOpen={isConnected && showChainModal && !isCorrectNetwork}
                onClose={() => setShowChainModal(false)}
                onSwitchChain={handleSwitchChain}
                currentChainName={getChainName(chainId)}
            />

            {/* Global referral gate: existing Staking/Yield page logic remains untouched. */}
            <ReferralBindGateModal />

            {/* Warning Banner – only shown when connected on wrong chain */}
            {isConnected && !isCorrectNetwork && chainId && (
                <div className="bg-theme text-black py-2 px-4 text-center text-sm">
                    ⚠️ {t("chainSwitch.wrongNetwork")}:{" "}
                    {t("chainSwitch.currentlyConnectedTo")}{" "}
                    {getChainName(chainId)}.{" "}
                    <button
                        onClick={() => setShowChainModal(true)}
                        className="underline font-semibold hover:text-theme-hover"
                    >
                        {t("chainSwitch.switchToBSC")}
                    </button>
                </div>
            )}

            {/* Routes */}
            <AnimatePresence mode={prefersReducedMotion ? "sync" : "wait"} initial={!prefersReducedMotion}>
                <Routes>
                    <Route
                        path="/"
                        element={<Navigate to={`/dashboard${typeof window !== "undefined" ? window.location.search : ""}`} replace />}
                    />


                    <Route element={<StandardLayout />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/community" element={<Community />} />
                        <Route path="/staking" element={<Staking />} />
                        <Route path="/yield" element={<Yield />} />
                        <Route path="/yield-history" element={<YieldHistory />} />
                        <Route path="/staking-history" element={<StakingHistory />} />
                        <Route path="/history" element={<History />} />
                        <Route path="/bonus-history" element={<BonusHistory />} />
                        <Route path="/swap" element={<Swap />} />
                        <Route path="/income-report" element={<IncomeReport />} />
                        <Route path="/vault/:nl_id" element={<VaultDetails />} />
                        <Route path="/news" element={<News />} />
                        <Route path="/news/:ann_id" element={<NewsDetails />} />
                        {freeStaking &&
                            <>
                                <Route path="/free-staking" element={<FreeStaking />} />
                                {/* <Route path="/free-staking-history" element={<FreeStakingHistory />} /> */}
                            </>
                        }
                        <Route path="/deposit" element={<Deposit />} />
                        <Route path="/deposit-history" element={<DepositHistory />} />
                        <Route path="/deposit-vt" element={<VtDeposit />} />
                        {/* <Route path="/deposits/deposit2" element={<Deposit />} /> */}
                    </Route>
                </Routes>
            </AnimatePresence>
        </div>
    );
}

export default function App() {
    return (
        <MotionConfig reducedMotion="user">
            <Router>
                <LanguageProvider>
                    <WalletProvider>
                        <AppContent />
                    </WalletProvider>
                </LanguageProvider>
            </Router>
        </MotionConfig>
    );
}
